import { useState } from "react";
import { Plus, Pencil, Trash2, Package, AlertTriangle, Search } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { formatRupiah } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const emptyForm = {
  nama: "",
  kategori: "Umum",
  harga_beli: "",
  harga_jual: "",
  stok: "",
  stok_minimum: "5",
};

export default function StokBarang() {
  const { products, addProduct, updateProduct, deleteProduct } = useShop();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [query, setQuery] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const filtered = products.filter((p) =>
    p.nama.toLowerCase().includes(query.toLowerCase())
  );

  const openAdd = () => {
    setForm(emptyForm);
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      nama: p.nama,
      kategori: p.kategori,
      harga_beli: String(p.harga_beli),
      harga_jual: String(p.harga_jual),
      stok: String(p.stok),
      stok_minimum: String(p.stok_minimum),
    });
    setEditId(p.id);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.nama.trim()) {
      toast.error("Nama barang wajib diisi");
      return;
    }
    const payload = {
      nama: form.nama.trim(),
      kategori: form.kategori.trim() || "Umum",
      harga_beli: Number(form.harga_beli) || 0,
      harga_jual: Number(form.harga_jual) || 0,
      stok: Number(form.stok) || 0,
      stok_minimum: Number(form.stok_minimum) || 0,
    };
    try {
      if (editId) await updateProduct(editId, payload);
      else await addProduct(payload);
      setOpen(false);
    } catch (e) {
      toast.error("Gagal menyimpan barang");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteProduct(toDelete.id);
    } catch (e) {
      toast.error("Gagal menghapus");
    }
    setToDelete(null);
  };

  const field = (k) => ({
    value: form[k],
    onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })),
  });

  const inputCls =
    "w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-900";

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-8 pt-8 pb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-2xl text-slate-900">Stok Barang</h2>
          <p className="text-sm text-slate-500 mt-1">Kelola produk, harga, dan jumlah stok</p>
        </div>
        <button
          data-testid="btn-tambah-barang"
          onClick={openAdd}
          className="bg-blue-600 text-white hover:bg-blue-700 font-semibold rounded-lg px-5 py-3 transition-colors active:scale-95 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" /> Tambah Barang
        </button>
      </div>

      <div className="px-8 pb-4">
        <div className="relative max-w-md">
          <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="input-cari-stok"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari barang..."
            className="w-full bg-white border border-slate-200 rounded-xl pl-12 pr-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-900"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-8 pb-8">
        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-left py-4 px-5">Nama Barang</th>
                <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-left py-4 px-5">Kategori</th>
                <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-right py-4 px-5">Harga Beli</th>
                <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-right py-4 px-5">Harga Jual</th>
                <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-right py-4 px-5">Laba/pcs</th>
                <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-center py-4 px-5">Stok</th>
                <th className="text-slate-600 font-semibold text-xs uppercase tracking-wider text-right py-4 px-5">Aksi</th>
              </tr>
            </thead>
            <tbody data-testid="tabel-stok">
              {filtered.map((p) => {
                const low = p.stok <= p.stok_minimum;
                return (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`stok-row-${p.id}`}>
                    <td className="py-4 px-5 font-semibold text-slate-900">{p.nama}</td>
                    <td className="py-4 px-5 text-slate-600">{p.kategori}</td>
                    <td className="py-4 px-5 text-right text-slate-600">{formatRupiah(p.harga_beli)}</td>
                    <td className="py-4 px-5 text-right font-medium text-slate-900">{formatRupiah(p.harga_jual)}</td>
                    <td className="py-4 px-5 text-right text-emerald-600 font-medium">{formatRupiah(p.harga_jual - p.harga_beli)}</td>
                    <td className="py-4 px-5 text-center">
                      <span
                        data-testid={`stok-value-${p.id}`}
                        className={`inline-flex items-center gap-1 font-bold px-3 py-1 rounded-full text-sm ${
                          low ? "bg-red-50 text-red-600" : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {low && <AlertTriangle className="w-3.5 h-3.5" />}
                        {p.stok}
                      </span>
                    </td>
                    <td className="py-4 px-5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`btn-edit-${p.id}`}
                          onClick={() => openEdit(p)}
                          aria-label="Edit"
                          className="w-9 h-9 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600 transition-colors flex items-center justify-center"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`btn-hapus-${p.id}`}
                          onClick={() => setToDelete(p)}
                          aria-label="Hapus"
                          className="w-9 h-9 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors flex items-center justify-center"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Package className="w-12 h-12 mb-3" strokeWidth={1.3} />
              <p>Belum ada barang</p>
            </div>
          )}
        </div>
      </div>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-barang">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Barang" : "Tambah Barang"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Nama Barang</label>
              <input data-testid="form-nama" {...field("nama")} className={`${inputCls} mt-1`} placeholder="Contoh: Indomie Goreng" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Kategori</label>
              <input data-testid="form-kategori" {...field("kategori")} className={`${inputCls} mt-1`} placeholder="Makanan / Minuman / dll" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Harga Beli</label>
                <input data-testid="form-harga-beli" type="number" {...field("harga_beli")} className={`${inputCls} mt-1`} placeholder="0" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Harga Jual</label>
                <input data-testid="form-harga-jual" type="number" {...field("harga_jual")} className={`${inputCls} mt-1`} placeholder="0" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Stok</label>
                <input data-testid="form-stok" type="number" {...field("stok")} className={`${inputCls} mt-1`} placeholder="0" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Stok Minimum</label>
                <input data-testid="form-stok-minimum" type="number" {...field("stok_minimum")} className={`${inputCls} mt-1`} placeholder="5" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setOpen(false)}
              className="bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium rounded-lg px-5 py-2.5 transition-colors"
            >
              Batal
            </button>
            <button
              data-testid="btn-simpan-barang"
              onClick={submit}
              className="bg-blue-600 text-white hover:bg-blue-700 font-semibold rounded-lg px-5 py-2.5 transition-colors active:scale-95"
            >
              Simpan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent data-testid="dialog-hapus">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus barang?</AlertDialogTitle>
            <AlertDialogDescription>
              Barang "{toDelete?.nama}" akan dihapus permanen. Aksi ini tidak bisa dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-konfirmasi-hapus" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
